import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, MapPin, DollarSign, Download } from 'lucide-react';
import SEO, { createEventStructuredData } from '../components/SEO';

export default function JimRootPage() {
  const entryFees = {
    early: [
      { type: "Team", price: "$425", description: "6-14 wrestlers" },
      { type: "Individual", price: "$50", description: "up to 5 wrestlers" },
      { type: "Both Tournaments", price: "$725", description: "Jim Root + Kusumoto Klassic" }
    ],
    late: [
      { type: "Team", price: "$450", description: "6-14 wrestlers" },
      { type: "Both Tournaments", price: "$750", description: "Jim Root + Kusumoto Klassic" },
      { type: "Extras", price: "$25", description: "per person beyond 14 wrestlers" }
    ]
  };

  return (
    <>
      <SEO
        title="63rd Annual Jim Root Memorial Wrestling Tournament"
        description="63rd Annual Jim Root Memorial Boys Wrestling Tournament on January 16, 2027 at Prospect High School, Saratoga. NFHS sanctioned 32-man double elimination brackets. Register now!"
        keywords={[
          'Jim Root Memorial',
          'boys wrestling tournament',
          'Prospect High School wrestling',
          'Saratoga wrestling tournament',
          'NFHS wrestling',
          'California high school wrestling',
          'January 2027 wrestling',
          'double elimination wrestling',
          'Bay Area wrestling tournament'
        ]}
        canonical="https://jimrootwrestling.com/jim-root"
        structuredData={createEventStructuredData(
          '63rd Annual Jim Root Memorial Wrestling Tournament',
          '2027-01-16',
          'boys'
        )}
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-900 to-black">
      {/* Navigation Header */}
      <header className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-md border-b border-gray-800">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-xl md:text-2xl font-bold bg-gradient-to-r from-red-500 to-yellow-500 bg-clip-text text-transparent">
              Jim Root Memorial
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link>
            <Link to="/jim-root" className="text-white transition-colors">Jim Root</Link>
            <Link to="/kusumoto" className="text-gray-300 hover:text-white transition-colors">Kusumoto</Link>
            <Link to="/register" className="text-gray-300 hover:text-white transition-colors">Registration</Link>
          </div>

          <Link
            to="/register"
            className="bg-red-600 hover:bg-red-700 px-6 py-2 rounded-full font-semibold transition-colors"
          >
            Register Now
          </Link>
        </nav>
      </header>

      <div className="container mx-auto px-4 py-8 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-blue-600 text-white">NFHS Sanctioned</Badge>
            <h1 className="text-5xl font-bold text-white mb-2">
              63rd Annual Jim Root Memorial
            </h1>
            <p className="text-xl text-gray-300">Wrestling Tournament</p>

            {/* Download Flier Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6"
            >
              <a
                href="/fliers/jim-root-2027.pdf"
                download
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Download className="w-6 h-6" />
                Download Tournament Flier
              </a>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-slate-800/50 backdrop-blur border-slate-700">
                <CardHeader className="pb-3">
                  <Calendar className="w-8 h-8 text-blue-400 mb-2" />
                  <CardTitle className="text-white">Date</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Saturday</p>
                  <p className="text-2xl font-bold text-blue-400">January 16, 2027</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-slate-800/50 backdrop-blur border-slate-700">
                <CardHeader className="pb-3">
                  <MapPin className="w-8 h-8 text-blue-400 mb-2" />
                  <CardTitle className="text-white">Location</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">Prospect High School</p>
                  <p className="text-sm text-gray-400">18900 Prospect Rd, Saratoga, CA 95070</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Registration Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <Card className="bg-slate-800/50 backdrop-blur border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                  Registration Fees
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Save money by registering before January 1st!
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-green-400 mb-3">
                    Early Registration (Paid by January 1st)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {entryFees.early.map((fee, index) => (
                      <div key={index} className="bg-slate-700/50 p-4 rounded-lg">
                        <p className="text-white font-semibold">{fee.type}</p>
                        <p className="text-2xl font-bold text-green-400">{fee.price}</p>
                        <p className="text-xs text-gray-400">{fee.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator className="bg-slate-600" />

                <div>
                  <h3 className="text-lg font-semibold text-orange-400 mb-3">
                    Late Registration (Paid after January 1st)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {entryFees.late.map((fee, index) => (
                      <div key={index} className="bg-slate-700/50 p-4 rounded-lg">
                        <p className="text-white font-semibold">{fee.type}</p>
                        <p className="text-2xl font-bold text-orange-400">{fee.price}</p>
                        <p className="text-xs text-gray-400">{fee.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

              </CardContent>
            </Card>
          </motion.div>

          <p className="text-center text-sm text-gray-400 mb-8">
            Schedule, weigh-ins, weight classes, awards and seeding are all on the
            flier &mdash;{' '}
            <a href="/fliers/jim-root-2027.pdf" download className="text-blue-400 hover:text-blue-300 underline">
              download it here
            </a>.
          </p>

        </motion.div>
      </div>
    </div>
    </>
  );
}
